import "../styles/records.css";
import type { HallOfRecords, HallView } from "@highmarch/app";
import { BalanceData, RaceWeekRecord, trialEndsAt, trialWeek, utcDay } from "@highmarch/domain";
import { btnHtml, fmtCompact } from "../kit";
import { icon } from "../iconsKit";
import { rowHtml, secHtml, sheetHeadHtml, tagHtml, tickTimers, timerChipHtml } from "../components";
import { paintedIconHtml } from "../featureIcons";
import { infinityGlyph, ironStarGlyph } from "../modeGlyphs";
import { fmtClear } from "../modes/challengeWords";
import { fmtLeft } from "../hub/eventsSheet";

/** What the Hall of Records sheet needs. */
export interface RecordsSheetCtx {
  readonly balance: BalanceData;
  readonly hall: HallOfRecords;
  readonly now: () => number;
  /** Shares the records card (null: no share sheet or clipboard here, SHARE is left out). */
  readonly share: ((v: HallView) => void) | null;
}

/** The sheet header's painted portrait (CSS px), as the Achievements and Almanac heads. */
const HEAD_ICON_PX = 52;
/** A section header's lead glyph (components.css .k-sec: 18 px beside the 20 px title). */
const SEC_ICON_PX = 18;
/** A tile's lead art. */
const TILE_ART_PX = 34;
/** A medal in the Race Week strip, and a medal or a code's scroll on a row. */
const MEDAL_PX = 40, ROW_MEDAL_PX = 30;
/** A slim empty row's dimmed art (UI pass 10-21: the big dashed wells went). */
const NONE_ART_PX = 26;

/** An empty list: one slim row, its art dimmed, a few words. */
function noneHtml(art: string, caption: string): string {
  return `<div class="hrnone"><span class="hrnart">${art}</span><span>${caption}</span></div>`;
}
const MEDAL_ICON = { gold: "achievementGold", silver: "achievementSilver", bronze: "achievementBronze" } as const;

function tile(kind: string, art: string, label: string, value: string, sub: string): string {
  return `<div class="hrtile ${kind}${value !== "—" && value !== "0" ? " has" : ""}"><span class="hrart">${art}</span><small class="disp">${label}</small><b class="disp">${value}</b><span class="hrsub">${sub}</span></div>`;
}

function tilesHtml(v: HallView, now: number): string {
  const e = v.endless, week = trialWeek(now);
  const endless = e.open || e.best.waves > 0
    ? tile("endless", infinityGlyph(TILE_ART_PX - 6), "ENDLESS BEST", e.best.waves > 0 ? `Wave ${e.best.waves}` : "—",
      `${e.week.waves > 0 ? `This week: wave ${e.week.waves}` : ""}${timerChipHtml(fmtLeft(trialEndsAt(week) - now), { label: "resets", endsAt: trialEndsAt(week), inline: true })}`)
    : tile("endless off", infinityGlyph(TILE_ART_PX - 6), "ENDLESS BEST", "—", `${icon("lock", 11)} Later`);
  const fastest = v.fastest;
  return `<div class="hrtiles k-grid2">
    ${endless}
    ${tile("iron", ironStarGlyph(TILE_ART_PX), "IRON STARS", String(v.iron.stars), v.iron.levels > 0 ? `${v.iron.levels} challenge${v.iron.levels === 1 ? "" : "s"} open` : "")}
    ${tile("stars", `<span class="hrstar">${icon("star", TILE_ART_PX - 8)}</span>`, "CAMPAIGN STARS", String(v.stars.have), `of ${v.stars.max}`)}
    ${tile("fastest", `<span class="hrclock">${icon("clock", TILE_ART_PX - 8)}</span>`, "FASTEST CLEAR", fastest ? fmtClear(fastest.ms) : "—", fastest ? `Level ${fastest.level}` : "")}
  </div>`;
}

function raceRow(r: RaceWeekRecord): string {
  const lead = r.medal ? paintedIconHtml(MEDAL_ICON[r.medal], ROW_MEDAL_PX) : `<span class="hrnomedal">${icon("flag", 18)}</span>`;
  return rowHtml({ lead, title: r.week.replace("-W", " · Week "), sub: `Level ${r.level}${r.medal ? ` · ${r.medal} medal` : " · no medal"}`, end: `<b class="hrtime disp">${fmtClear(r.ms)}</b>`, compact: true, className: "hrrow" });
}

function raceHtml(v: HallView): string {
  const m = v.medals, total = m.gold + m.silver + m.bronze;
  const strip = `<div class="hrmedals">${(["gold", "silver", "bronze"] as const).map((k) => `<div class="hrmedal m-${k}${m[k] === 0 ? " none" : ""}">${paintedIconHtml(MEDAL_ICON[k], MEDAL_PX)}<b class="disp">${m[k]}</b></div>`).join("")}</div>`;
  const body = v.races.length === 0 ? "" : `<div class="hrlist">${v.races.map(raceRow).join("")}</div>`;
  return `<section class="hrsec race">${secHtml("Race Week", total > 0 ? `${total} medal${total === 1 ? "" : "s"}` : "", icon("flag", SEC_ICON_PX))}${strip}${body}</section>`;
}

function dailyHtml(v: HallView, ctx: RecordsSheetCtx): string {
  const days = ctx.balance.modes.records.dailyDays, today = utcDay(ctx.now());
  const byDay = new Map(v.daily.map((e) => [e.day, e]));
  const cells = Array.from({ length: days }, (_, i) => today - days + 1 + i).map((d) => {
    const e = byDay.get(d);
    return `<i class="hrday${e ? (e.won ? " won" : " played") : ""}${d === today ? " today" : ""}" title="${e ? fmtCompact(e.score) : ""}"></i>`;
  }).join("");
  const best = v.daily.reduce((b, e) => Math.max(b, e.score), 0), won = v.daily.filter((e) => e.won).length;
  const summary = v.daily.length === 0 ? ""
    : `<p class="hrnote">${icon("trophy", 14)} Best <b>${fmtCompact(best)}</b> · ${won} won of ${v.daily.length} played</p>`;
  return `<section class="hrsec daily">${secHtml("Daily Road", `last ${days} days`, paintedIconHtml("dailyRoad", SEC_ICON_PX + 4))}
    <div class="hrstrip" style="--days:${days}">${cells}</div>${summary}</section>`;
}

function clearsHtml(B: BalanceData, v: HallView): string {
  // the class says first clear or replay ("campaign" is the campaign map screen's own class)
  const time = (ms: number | undefined, kind: "first" | "replay") => (ms ? `<span class="hrct ${kind}"><small>${kind === "first" ? "FIRST" : "REPLAY"}</small><b class="disp">${fmtClear(ms)}</b></span>` : "");
  const rows = [...v.clears].reverse().map((r) => {
    const best = Math.min(r.times.campaign ?? Infinity, r.times.farm ?? Infinity);
    return rowHtml({ lead: `<span class="hrlvl disp">${r.level}</span>`, title: B.level(r.level).name, sub: `Level ${r.level}${v.fastest?.level === r.level && v.fastest.ms === best ? ` ${tagHtml("best", "FASTEST")}` : ""}`,
      end: `${time(r.times.campaign, "first")}${time(r.times.farm, "replay")}`, compact: true, className: "hrrow clear" });
  }).join("");
  const body = v.clears.length === 0 ? noneHtml(icon("clock", 18), "No clears yet") : `<div class="hrlist">${rows}</div>`;
  return `<section class="hrsec clears">${secHtml("Fastest clears", v.clears.length > 0 ? `${v.clears.length} level${v.clears.length === 1 ? "" : "s"}` : "", icon("clock", SEC_ICON_PX))}${body}</section>`;
}

function challengesHtml(v: HallView): string {
  const rows = v.challenges.map((c) => rowHtml({
    lead: paintedIconHtml("challenge", ROW_MEDAL_PX), title: `<span class="hrcodetxt">${c.code}</span>`, sub: `Level ${c.level} · ${c.runs} battle${c.runs === 1 ? "" : "s"}`,
    end: c.won ? `<b class="hrtime disp">${fmtClear(c.ms)}</b>` : `<span class="hrwave">wave ${c.stages}</span>`, compact: true, className: `hrrow code${c.won ? " won" : ""}`,
  })).join("");
  const body = v.challenges.length === 0 ? noneHtml(paintedIconHtml("challenge", NONE_ART_PX), "No codes played yet") : `<div class="hrlist">${rows}</div>`;
  return `<section class="hrsec codes">${secHtml("Challenge codes", v.challenges.length > 0 ? `${v.challenges.length} played` : "", paintedIconHtml("challenge", SEC_ICON_PX + 4))}${body}</section>`;
}

/**
 * ⚙ → Hall of Records (MP-27, docs/MODES.md §8), beside Achievements and the Almanac: the personal bests (Endless,
 * Iron stars, campaign stars, the fastest clear), Race Week medals and times, the Daily Road's last days as a strip,
 * every level's fastest clears and the challenge codes played. SHARE hands the records card to the share sheet.
 */
export function showRecordsSheet(parent: HTMLElement, ctx: RecordsSheetCtx): HTMLElement {
  parent.querySelector(":scope > .sheet.records")?.remove();
  const sheet = document.createElement("div");
  sheet.className = "sheet panel records";
  parent.append(sheet);
  const now = ctx.now(), v = ctx.hall.view(now);
  const sub = v.hasAny ? "Your finest hours, carved in stone" : "";
  sheet.innerHTML = `${sheetHeadHtml({ title: "Hall of Records", sub, portrait: paintedIconHtml("records", HEAD_ICON_PX), tint: "gold" })}
    <div class="hrbody">${tilesHtml(v, now)}${raceHtml(v)}${dailyHtml(v, ctx)}${clearsHtml(ctx.balance, v)}${challengesHtml(v)}
      ${ctx.share && v.hasAny ? btnHtml("SHARE MY RECORDS", "gold", { icon: "star", className: "disp go hrshare" }) : ""}</div>`;
  sheet.querySelector<HTMLButtonElement>(".close")!.onclick = () => { clearInterval(timer); sheet.remove(); };
  sheet.querySelector<HTMLButtonElement>(".hrshare")?.addEventListener("click", () => ctx.share?.(v));
  const timer = setInterval(() => { if (!sheet.isConnected) clearInterval(timer); else tickTimers(sheet, ctx.now(), fmtLeft); }, 1000);
  return sheet;
}
